/**
 * Layers drawer: planning layer (radio), project boundaries (toggles), overlay opacity,
 * basemap picker and a searchable legend generated from the live layer metadata.
 * Also drives the "active layer" chip and keeps preferences + URL hash in sync.
 */
import { labelsOn, setLabels } from './labels.js';
import { placesOn, setPlaces, placesInfo } from './places.js';
import { giaLayerOn, setGiaLayer, giaLegend } from './gia.js';
import { PLACE_GROUPS, placeIconURL } from './placestyle.js';
import { $, $$, esc, icon, fmt, toast, hideAnimated, isMobile, setPressed, scrollHints, scrollWithin } from './ui.js';
import { api } from './api.js';
import { bus, prefs, savePrefs, writeHash, basemapId, theme } from './state.js';
import { PLAN_LAYERS, PLAN_BY_NO, BOUNDARY_LAYERS, BASEMAPS, TRANSPORT_LEGEND, SDD_LEGEND, planFrom } from './config.js';
import { SDD, sddCatalog, loadSddCatalog, sddPeriod, setSddPeriod } from './sddlayer.js';
import { GT, prepareTransport, transportColors } from './transport.js';
import { bboxIntersects, haversine } from './core/geo.js';
import { fold } from './core/text.js';
import { vectorManifest } from './vectorplan.js';
import { vectorLayerOf, typesForLegendItem, typeStats } from './core/vplan.js';
import * as panel from './panel.js';

let pm = null;
let meta = null;
const els = {};
const legendState = { q: '', all: false };
const LEGEND_PREVIEW = 14;
/** legend highlight of the vector QH 1/2000 layer: picked category and its manifest type indices */
const hl = { plan: null, key: null, label: '', hex: '', ts: [] };
const legendKey = (it) => fold(it.label) + '|' + (it.hex || '').toUpperCase();
const vectorLegend = (plan) => !!vectorLayerOf(vectorManifest(), plan);
let typeCache = { man: null, plan: null, map: null };
/** manifest type indices of a legend item (cached per plan; [] = no cell of that kind) */
function itemTypes(plan, it) {
  const man = vectorManifest();
  if (typeCache.man !== man || typeCache.plan !== plan) typeCache = { man, plan, map: new Map() };
  const k = legendKey(it);
  if (!typeCache.map.has(k)) typeCache.map.set(k, typesForLegendItem(man, plan, legendOf(plan), it));
  return typeCache.map.get(k);
}

export function initLayers(planMap) {
  pm = planMap;
  els.drawer = $('#drawer');
  els.body = $('#drawer-body');
  els.btn = $('#btn-layers');
  els.chip = $('#layer-chip');
  els.pill = document.createElement('div');
  els.pill.className = 'hl-pill glass';
  els.pill.setAttribute('role', 'status');
  els.pill.hidden = true;
  ($('#dock') || document.body).appendChild(els.pill);
  els.pill.addEventListener('click', (e) => {
    if (!e.target.closest('[data-act="hl-clear"]')) return;
    setHighlight(null, null);
    if (isDrawerOpen()) renderLegend();
  });
  els.btn.addEventListener('click', () => toggleDrawer());
  $('#drawer-close').addEventListener('click', () => closeDrawer());
  els.body.addEventListener('click', onClick);
  els.body.addEventListener('input', onInput);
  els.body.addEventListener('change', onChange);
  els.body.addEventListener('keydown', onKey);
  scrollHints(els.body, els.drawer);

  api.meta().then((m) => { meta = m; renderChip(); if (isDrawerOpen()) render(); }).catch(() => { /* legend falls back to names only */ });
  renderChip();

  bus.on('theme', () => {
    // basemap follows the theme unless the user picked one explicitly
    if (!prefs.basemap) pm.setBasemap(basemapId()).then(() => bus.emit('basemap', pm.basemap));
    else pm.refreshColors();
    if (isDrawerOpen()) renderBasemaps();
  });
  bus.on('set-plan', (n) => setPlan(n, { announce: true }));
  bus.on('basemap', () => { if (isDrawerOpen()) renderBasemaps(); if (prefs.plan === GT) renderChip(); });
  bus.on('selection', () => { if (isDrawerOpen()) renderLegend(); });
  bus.on('sdd-catalog', () => { if (prefs.plan === SDD) { renderChip(); if (isDrawerOpen()) renderLegend(); } });
  bus.on('sdd-period', () => { renderChip(); if (isDrawerOpen()) renderLegend(); coverageHint(SDD); });
  bus.on('gia-layer', (on) => {
    if (!els.body) return;
    const cb = $('#gia-toggle', els.body), lg = $('#gia-legend', els.body);
    if (cb) cb.checked = on;
    if (lg) lg.hidden = !on;
  });
  bus.on('places-info', () => { const el = els.body && $('#places-sub', els.body); if (el) el.textContent = placesSub(); });
}

export const isDrawerOpen = () => !els.drawer.hidden && !els.drawer.classList.contains('closing');

export function openDrawer({ legend = false } = {}) {
  if (els.drawer.classList.contains('closing')) { els.drawer.classList.remove('closing'); setPressed(els.btn, true); }
  if (!isDrawerOpen()) {
    render();
    els.drawer.hidden = false;
    setPressed(els.btn, true);
    bus.emit('drawer', true);
  }
  if (legend) requestAnimationFrame(() => scrollWithin(els.body, $('#legend-section', els.body), { block: 'start', behavior: 'smooth', margin: 4 }));
}

export async function closeDrawer() {
  if (!isDrawerOpen()) return;
  setPressed(els.btn, false);
  if (!(await hideAnimated(els.drawer, 'closing', 200))) return;
  bus.emit('drawer', false);
}

export function toggleDrawer() { return isDrawerOpen() ? closeDrawer() : openDrawer(); }

/* ---------------- state changes ---------------- */

export function setPlan(n, { announce = false } = {}) {
  const plan = n && PLAN_BY_NO[n] ? n : null;
  if (plan === prefs.plan) return;
  prefs.plan = plan;
  savePrefs();
  setHighlight(null, null);
  pm.setPlan(plan);
  writeHash({});
  legendState.q = ''; legendState.all = false;
  renderChip();
  if (isDrawerOpen()) render();
  bus.emit('plan', plan);
  if (announce && plan) toast(`Đang hiển thị: ${PLAN_BY_NO[plan].short}`, { type: 'ok', ms: 1800 });
  if (plan === GT) {
    prepareTransport();
    if (pm.map && pm.map.getZoom() < 11) {
      toast('Phóng to để xem mạng lưới đường quy hoạch', { type: 'info', ms: 6000, action: { label: 'Phóng tới nội đô', onClick: () => pm.goTo({ lat: 21.0245, lng: 105.8412 }, { zoom: 13.2, padding: panel.viewPadding() }) } });
      return;
    }
  }
  if (plan) coverageHint(plan);
}

function setBoundary(n, on) {
  const set = new Set(prefs.boundaries);
  if (on) set.add(n); else set.delete(n);
  prefs.boundaries = [...set].sort((a, b) => a - b);
  savePrefs();
  pm.setBoundaries(prefs.boundaries);
  writeHash({});
  bus.emit('boundaries', prefs.boundaries);
}

function setOpacity(v) {
  prefs.opacity = v;
  savePrefs();
  pm.setOpacity(v);
  bus.emit('opacity', v);
}

export async function setBasemap(id) {
  // choosing the theme's own default means "follow the theme" again
  const themeDefault = theme() === 'dark' ? 'dark' : 'positron';
  prefs.basemap = id === themeDefault ? null : id;
  savePrefs();
  writeHash({});
  const pending = pm.setBasemap(id); // sets pm.basemap synchronously
  renderBasemaps();
  const ok = await pending;
  if (ok === false && pm.basemap === id) toast('Không tải được bản đồ nền, vui lòng thử lại', { type: 'err' });
  bus.emit('basemap', pm.basemap);
}

/**
 * The detailed layers only cover the areas of approved projects. When the current view has no
 * data for the chosen layer, offer a one-click jump to the nearest covered area.
 */
async function coverageHint(plan) {
  if (plan === SDD) return sddCoverageHint();
  const boundLayer = plan === 673 ? 674 : plan === 654 || plan === GT ? 659 : null;
  if (!boundLayer || !pm.map) return;
  let items;
  try { items = (await api.projects()).filter((p) => p.layerNo === boundLayer && p.bbox); } catch { return; }
  if (!items.length || prefs.plan !== plan) return;
  const b = pm.map.getBounds();
  const view = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  if (items.some((p) => bboxIntersects(p.bbox, view))) return;
  const c = pm.map.getCenter();
  const near = items
    .map((p) => ({ p, d: haversine(c.lat, c.lng, p.center?.[1] ?? (p.bbox[1] + p.bbox[3]) / 2, p.center?.[0] ?? (p.bbox[0] + p.bbox[2]) / 2) }))
    .sort((x, y) => x.d - y.d)[0];
  toast(`Khu vực đang xem chưa có dữ liệu ${PLAN_BY_NO[plan].short}`, {
    type: 'info', ms: 7000,
    action: { label: `Đến đồ án gần nhất (${fmt.dist(near.d)})`, onClick: () => pm.fitBBox(near.p.bbox, { padding: panel.viewPadding(), maxZoom: 16.5 }) },
  });
}

/* ---------------- rendering ---------------- */

function legendOf(plan) { return meta?.layers?.[plan]?.legend || null; }
function countOf(n) { return meta?.layers?.[n]?.featureCount; }

function renderChip() {
  const plan = prefs.plan;
  const name = $('#layer-chip-name');
  const sw = $('#layer-chip-swatches');
  if (!plan) {
    name.textContent = 'Chỉ bản đồ nền';
    sw.innerHTML = `<span class="swatch" style="--sw:var(--surface-3)"></span>`;
    return;
  }
  name.textContent = PLAN_BY_NO[plan].short;
  els.chip.setAttribute('aria-label', `Lớp quy hoạch đang hiển thị: ${PLAN_BY_NO[plan].short}. Mở chú giải`);
  if (plan === SDD) {
    const per = sddPeriod();
    if (per) name.textContent = per.short;
    sw.innerHTML = ['#ffa0ff', '#fffc82', '#ffaa32'].map((c) => `<span class="swatch" style="--sw:${c}"></span>`).join('');
    return;
  }
  if (plan === GT) {
    const t = gtPalette();
    sw.innerHTML = [t.road, t.metro, t.rail].map((c) => `<span class="swatch" style="--sw:${esc(c)}"></span>`).join('');
    return;
  }
  const items = legendOf(plan)?.items || [];
  const seen = new Set();
  const colors = [];
  for (const it of items) { const h = it.hex?.toUpperCase(); if (h && !seen.has(h)) { seen.add(h); colors.push(h); } if (colors.length === 3) break; }
  sw.innerHTML = (colors.length ? colors : ['#94a3b8']).map((c) => `<span class="swatch" style="--sw:${esc(c)}"></span>`).join('');
}

/**
 * Re-rendering replaces the controls; put keyboard focus back on the equivalent control (by id
 * or data attribute) instead of losing it to <body>.
 */
function keepFocus(root, draw) {
  const a = document.activeElement;
  let key = null;
  if (root && a && a !== root && root.contains(a)) {
    const d = a.dataset;
    key = a.id ? `#${CSS.escape(a.id)}` : d.k ? `[data-k="${CSS.escape(d.k)}"]` : d.plan != null ? `[data-plan="${d.plan}"]` : d.bound ? `[data-bound="${d.bound}"]` : d.act ? `[data-act="${d.act}"]` : null;
  }
  let sel = null;
  try { if (key && a.selectionStart != null) sel = [a.selectionStart, a.selectionEnd]; } catch { /* not a text field */ }
  draw();
  if (!key) return;
  const n = $(key, root);
  if (!n || n === document.activeElement) return;
  n.focus({ preventScroll: true });
  if (sel) try { n.setSelectionRange(...sel); } catch { /* not a text field */ }
}

function render() { keepFocus(els.body, drawAll); }
function renderLegend() { keepFocus($('#legend-section', els.body), drawLegend); }

function drawAll() {
  els.body.innerHTML = `
    <section aria-labelledby="g-plan">
      <div class="group-title" id="g-plan">Lớp quy hoạch<span>${meta ? fmt.num(PLAN_LAYERS.reduce((s, l) => s + (countOf(l.layerNo) || 0), 0)) + ' đối tượng' : ''}</span></div>
      <div role="radiogroup" aria-labelledby="g-plan" id="plan-group">
        ${PLAN_LAYERS.map((l) => planOption(l)).join('')}
        ${planOption(null)}
      </div>
      <div class="range">
        <label for="opacity-range">Độ đậm lớp quy hoạch</label>
        <input type="range" id="opacity-range" min="15" max="100" step="1" value="${Math.round(prefs.opacity * 100)}" style="--p:${pctPos(prefs.opacity * 100)}%" ${prefs.plan ? '' : 'disabled'}>
        <output for="opacity-range" id="opacity-out">${Math.round(prefs.opacity * 100)}%</output>
      </div>
      <label class="toggle-row">
        <input type="checkbox" id="labels-toggle" ${labelsOn() ? 'checked' : ''}>
        <span class="switch" aria-hidden="true"></span>
        <span class="grow">Nhãn trên bản đồ<small>Tên loại đất trên từng ô từ mức phóng 14 (QH 100 năm từ 15); lớp Giao thông ghi lộ giới từng đoạn đường từ mức phóng 16</small></span>
      </label>
    </section>
    <section aria-labelledby="g-bound">
      <div class="group-title" id="g-bound">Ranh giới đồ án</div>
      ${BOUNDARY_LAYERS.map((b) => `
        <label class="toggle-row">
          <input type="checkbox" data-bound="${b.layerNo}" ${prefs.boundaries.includes(b.layerNo) ? 'checked' : ''}>
          <span class="switch" aria-hidden="true"></span>
          <span class="grow">${esc(b.title)}<small>${esc(b.sub)}${countOf(b.layerNo) ? ` · ${fmt.num(countOf(b.layerNo))} đồ án` : ''}</small></span>
          <span class="line-sample" style="border-top-color:${esc(meta?.layers?.[b.layerNo]?.legend?.line || '#e31010')}" aria-hidden="true"></span>
        </label>`).join('')}
      <button type="button" class="link-row cat-entry" data-act="catalog">
        ${icon('library-big')}
        <span class="grow">Danh mục đồ án<small>Tìm theo mã, tên, quyết định · kể cả đồ án chưa số hoá</small></span>
        ${icon('chevron-right', 'sm')}
      </button>
    </section>
    <section aria-labelledby="g-bm">
      <div class="group-title" id="g-bm">Bản đồ nền<span>${prefs.basemap ? '' : 'Theo giao diện'}</span></div>
      <div id="bm-group"></div>
      <label class="toggle-row">
        <input type="checkbox" id="places-toggle" ${placesOn() ? 'checked' : ''}>
        <span class="switch" aria-hidden="true"></span>
        <span class="grow">Địa điểm và số nhà<small id="places-sub">${placesSub()}</small></span>
      </label>
      <div class="pl-legend" id="places-legend" ${placesOn() ? '' : 'hidden'}>${placeChips()}</div>
      <label class="toggle-row">
        <input type="checkbox" id="gia-toggle" ${giaLayerOn() ? 'checked' : ''}>
        <span class="switch" aria-hidden="true"></span>
        <span class="grow">Bảng giá đất 2026<small>Giá đất ở vị trí 1 theo từng đoạn đường (Nghị quyết 52/2025/NQ-HĐND), vị trí đoạn ước tính; bấm vào bản đồ để xem đủ VT1–VT4</small></span>
      </label>
      <div id="gia-legend" ${giaLayerOn() ? '' : 'hidden'}>${giaLegend()}</div>
      <button type="button" class="link-row cat-entry" data-act="gia-table">
        ${icon('table')}
        <span class="grow">Tra bảng giá đất<small>17 khu vực, 2.435 đoạn đường, tìm theo tên đường</small></span>
        ${icon('chevron-right', 'sm')}
      </button>
    </section>
    <section aria-labelledby="g-legend" id="legend-section"></section>
    <div class="note">${icon('info')}<div>Dữ liệu quy hoạch lấy trực tiếp từ <b>Hệ thống thông tin quy hoạch TP Hà Nội</b> (đang vận hành thử nghiệm). Thông tin chỉ có giá trị tham khảo, không thay thế văn bản cung cấp thông tin quy hoạch của cơ quan có thẩm quyền.</div></div>`;
  renderBasemaps();
  renderLegend();
}

function placesSub() {
  const inf = placesInfo();
  const n = inf ? `${fmt.num(Math.round(inf.count / 1000))} nghìn điểm` : 'hơn 180 nghìn điểm';
  return `Cửa hàng, quán ăn, trường, bệnh viện, cơ quan… (${n} từ Overture Maps và OpenStreetMap) hiện dần từ mức phóng 15; số nhà từ mức 17 ở nơi OpenStreetMap có dữ liệu`;
}

let chipsHtml = '';
function placeChips() {
  if (!chipsHtml) chipsHtml = PLACE_GROUPS.map((g) => `<span class="pl-chip"><img src="${placeIconURL(g, 2)}" alt="" width="14" height="14">${esc(g.label)}</span>`).join('');
  return chipsHtml;
}

function planOption(l) {
  const n = l ? l.layerNo : 0;
  const on = (prefs.plan || 0) === n;
  const count = l && countOf(n);
  const sub = l ? `${esc(l.sub)}${count ? ` · ${fmt.num(count)} ${esc(l.unit)}` : ''}` : 'Ẩn lớp quy hoạch, chỉ xem bản đồ nền';
  return `
    <button type="button" class="layer-opt" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-plan="${n}">
      <span class="lo-ico">${icon(l ? l.icon : 'eye-off')}</span>
      <span><span class="lo-title">${esc(l ? l.title : 'Không hiển thị')}</span><span class="lo-sub">${sub}</span></span>
      <span class="radio" aria-hidden="true"></span>
    </button>`;
}

function renderBasemaps() {
  const host = $('#bm-group', els.body);
  if (!host) return;
  const cur = BASEMAPS.find((b) => b.id === (pm.basemap || basemapId())) || BASEMAPS[0];
  host.innerHTML = `
    <button type="button" class="link-row bm-row" data-act="bm-tray">
      <img class="lr-thumb" src="/assets/img/thumbs/bm-${cur.id}-s.webp" alt="" width="240" height="150" decoding="async">
      <span class="grow"><b>${esc(cur.title)}</b><small>Xem trước và đổi kiểu bản đồ nền</small></span>${icon('chevron-right', 'sm')}
    </button>`;
  const t = host.closest('section')?.querySelector('.group-title span');
  if (t) t.textContent = prefs.basemap ? '' : 'Theo giao diện';
}

/** Category of the selected point on the given layer (to mark it in the legend). */
function selectedCategory(plan) {
  const s = panel.currentSelection();
  if (!s || plan === GT) return null;
  if (plan === 895) return s.qh100?.hit?.category || null;
  const lv = s.result?.levels;
  if (!lv) return null;
  if (plan === 673) return lv.find((l) => l.key === 'detail')?.cells[0]?.category || null;
  if (plan === 654) return lv.find((l) => l.key === 'zone')?.cells[0]?.category || null;
  if (plan === 666) return lv.find((l) => l.key === 'general')?.cells[0]?.category || null;
  if (plan === 667) return lv.find((l) => l.key === 'general')?.zoning?.[0]?.category || null;
  return null;
}

function drawLegend() {
  const host = $('#legend-section', els.body);
  if (!host) return;
  const plan = prefs.plan;
  if (!plan) { host.innerHTML = ''; return; }
  if (plan === GT) { host.innerHTML = transportLegend(); return; }
  if (plan === SDD) { host.innerHTML = sddLegend(); return; }
  const lg = legendOf(plan);
  if (!lg) {
    host.innerHTML = `<div class="group-title">Chú giải</div><div class="sk sk-block" style="height:120px"></div>`;
    return;
  }
  const vec = vectorLegend(plan);
  let items = dedupeLegend(lg.items || []);
  // vector layer: categories with cells first (the official legend also lists sea water, forests…)
  if (vec) items = [...items.filter((it) => itemTypes(plan, it).length), ...items.filter((it) => !itemTypes(plan, it).length)];
  const q = fold(legendState.q);
  const picked = selectedCategory(plan);
  const pickedKey = picked ? fold(picked) : null;
  const list = q ? items.filter((it) => fold(it.label).includes(q)) : items;
  const show = q || legendState.all ? list : list.slice(0, LEGEND_PREVIEW);
  // keep the selected category (and the highlighted one) visible even when the list is collapsed
  const hlKey = vec && hl.plan === plan ? hl.key : null;
  for (const keep of [(it) => pickedKey && fold(it.label) === pickedKey, (it) => hlKey && legendKey(it) === hlKey]) {
    if (show.some(keep)) continue;
    const hit = list.find(keep);
    if (hit) show.unshift(hit);
  }
  host.innerHTML = `
    <div class="group-title" id="g-legend">Chú giải · ${esc(PLAN_BY_NO[plan].short)}<span>${items.length} loại</span></div>
    ${vec ? '<p class="legend-hint">Bấm một loại đất để tô sáng các ô của nó trên bản đồ.</p>' : ''}
    ${items.length > 8 ? `<label class="legend-search">${icon('search')}<input type="search" id="legend-q" placeholder="Lọc loại đất, chức năng…" value="${esc(legendState.q)}" autocomplete="off" spellcheck="false" aria-label="Lọc chú giải"></label>` : ''}
    <ul class="legend${vec ? ' pickable' : ''}" aria-labelledby="g-legend">
      ${show.map((it) => {
        const on = pickedKey && fold(it.label) === pickedKey;
        const inner = `<span class="swatch" style="--sw:${esc(it.hex || '#94a3b8')}"></span><span class="grow">${esc(it.label)}</span>${on ? `<span class="badge accent">${icon('map-pin')}Vị trí chọn</span>` : ''}`;
        if (!vec || !itemTypes(plan, it).length) return `<li class="${on ? 'on' : ''}${vec ? ' none' : ''}">${inner}</li>`;
        const k = legendKey(it), sel = hlKey === k;
        return `<li class="${on ? 'on' : ''}${sel ? ' sel' : ''}"><button type="button" class="lg-btn" data-act="lg-hl" data-k="${esc(k)}" aria-pressed="${sel}">${inner}</button></li>`;
      }).join('') || `<li class="empty-line">Không có loại nào khớp “${esc(legendState.q)}”</li>`}
    </ul>
    ${!q && list.length > LEGEND_PREVIEW ? `<button type="button" class="legend-more btn-link" data-act="legend-all">${legendState.all ? 'Thu gọn' : `Xem tất cả ${list.length} loại`}</button>` : ''}`;
}

/** The chosen land-use plan period has no map for the area in view (e.g. QHSDĐ 2020 = 18 rural districts). */
async function sddCoverageHint() {
  try { await loadSddCatalog(); } catch { return; }
  const per = sddPeriod();
  if (!per || prefs.plan !== SDD || !pm.map) return;
  const b = pm.map.getBounds();
  const view = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  if (per.layers.some(([, , bb]) => bboxIntersects(bb, view))) return;
  const c = pm.map.getCenter();
  const near = per.layers
    .map(([, name, bb]) => ({ name, bb, d: haversine(c.lat, c.lng, (bb[1] + bb[3]) / 2, (bb[0] + bb[2]) / 2) }))
    .sort((x, y) => x.d - y.d)[0];
  if (!near) return;
  toast(`${per.short}: không có bản đồ cho khu vực đang xem`, {
    type: 'info', ms: 7000,
    action: { label: `Đến ${near.name}`, onClick: () => pm.fitBBox(near.bb, { padding: panel.viewPadding(), maxZoom: 14 }) },
  });
}

/** Legend of the land-use plan view: period picker, land-use codes, official legend image, notes. */
function sddLegend() {
  const cat = sddCatalog();
  if (!cat) {
    loadSddCatalog().catch(() => { const h = $('#legend-section', els.body); if (h && prefs.plan === SDD) h.innerHTML = `<div class="note warn">${icon('triangle-alert')}<div>Không tải được danh mục bản đồ kế hoạch sử dụng đất.</div></div>`; });
    return `<div class="group-title">Chú giải</div><div class="sk sk-block" style="height:120px"></div>`;
  }
  const per = sddPeriod();
  const items = SDD_LEGEND.map(([code, label, hex]) => ({ code, label, hex }));
  const q = fold(legendState.q);
  const list = q ? items.filter((it) => fold(`${it.label} ${it.code}`).includes(q)) : items;
  const show = q || legendState.all ? list : list.slice(0, LEGEND_PREVIEW);
  return `
    <div class="group-title" id="g-legend">Kế hoạch sử dụng đất<span>${per ? `${per.layers.length} bản đồ` : ''}</span></div>
    <label class="sdd-period"><span>Kỳ quy hoạch, kế hoạch</span>
      <select id="sdd-period">${cat.periods.map((p) => `<option value="${esc(p.id)}"${p === per ? ' selected' : ''}>${esc(p.title)}</option>`).join('')}</select>
    </label>
    ${per?.note ? `<div class="note warn">${icon('triangle-alert')}<div>${esc(per.note)}</div></div>` : ''}
    <label class="legend-search">${icon('search')}<input type="search" id="legend-q" placeholder="Lọc theo tên hoặc mã đất (ODT, LUA…)" value="${esc(legendState.q)}" autocomplete="off" spellcheck="false" aria-label="Lọc chú giải"></label>
    <ul class="legend" aria-labelledby="g-legend">
      ${show.map((it) => `<li><span class="swatch" style="--sw:${esc(it.hex)}"></span><span class="grow">${esc(it.label)}</span><span class="lg-code">${esc(it.code)}</span></li>`).join('') || `<li class="empty-line">Không có loại nào khớp “${esc(legendState.q)}”</li>`}
    </ul>
    ${!q && list.length > LEGEND_PREVIEW ? `<button type="button" class="legend-more btn-link" data-act="legend-all">${legendState.all ? 'Thu gọn' : `Xem tất cả ${list.length} loại`}</button>` : ''}
    <details class="sdd-img">
      <summary>${icon('image')}Bảng ký hiệu chính thức (đất, ranh giới, giao thông)</summary>
      <img src="${esc(cat.legend)}" alt="Chú giải bản đồ kế hoạch sử dụng đất của Sở Nông nghiệp và Môi trường Hà Nội" width="400" height="1743" loading="lazy" decoding="async">
    </details>
    <div class="note">${icon('info')}<div><b>Cách đọc:</b> khoanh tô màu kèm mã (ODT, LUA…) là hiện trạng sử dụng đất; khoanh ghi hai dòng – mã hiện trạng ở trên, mã mới chữ đỏ ở dưới, có thể gạch chéo – là đất có kế hoạch chuyển mục đích hoặc thu hồi trong kỳ.<br><b>Khác quy hoạch xây dựng:</b> kế hoạch sử dụng đất lập theo Luật Đất đai (căn cứ giao đất, thu hồi, chuyển mục đích), không thay thế quy hoạch phân khu, chi tiết. Bản đồ lập theo địa giới quận, huyện cũ (trước 1/7/2025), chỉ là ảnh nên không tra cứu được từng thửa tại đây.<br>Nguồn: <a href="${esc(cat.source)}" target="_blank" rel="noopener">${esc(cat.publisher)}</a>.</div></div>`;
}

/** Colours of the transport view (same as the tile worker / OSM overlay). */
function gtPalette() {
  const dark = theme() === 'dark';
  const c = transportColors(dark ? 'dark' : 'light', /imagery|hybrid/.test(pm.basemap || ''));
  return {
    road: dark ? 'rgb(96,146,255)' : 'rgb(0,71,171)',
    roadGen: dark ? 'rgba(96,146,255,.55)' : 'rgba(0,71,171,.55)',
    roadHatch: dark ? 'rgba(96,146,255,.3)' : 'rgba(0,71,171,.29)',
    bld: dark ? 'rgb(248,113,113)' : 'rgb(220,38,38)',
    park: 'rgb(234,88,12)',
    ctx: dark ? '#475569' : '#cbd5e1',
    ...c,
  };
}

function transportLegend() {
  const p = gtPalette();
  const sample = (it) => {
    if (it.kind === 'fill') return `<span class="swatch" style="--sw:${esc({ road: p.road, 'road-gen': p.roadGen, park: p.park, ctx: p.ctx, bld: p.bld }[it.tone])}"></span>`;
    if (it.kind === 'hatch') return `<span class="swatch hatch" style="--sw:${esc(p.road)};--sw2:${esc(p.roadHatch)}"></span>`;
    if (it.kind === 'metro') return `<span class="lg-line metro" style="--c:${p.metro}"></span>`;
    if (it.kind === 'rail') return `<span class="lg-line rail" style="--c:${p.rail};--g:${p.railGap}"></span>`;
    if (it.kind === 'construction') return `<span class="lg-line constr" style="--c:${p.construction}"></span>`;
    if (it.kind === 'text') return `<span class="lg-text" style="--c:${theme() === 'dark' ? '#8fb4ff' : '#0047ab'}">21,5</span>`;
    return `<span class="lg-dot" style="--c:${p.metro}"></span>`;
  };
  return `
    <div class="group-title" id="g-legend">Chú giải · Quy hoạch giao thông<span>${TRANSPORT_LEGEND.length} mục</span></div>
    <ul class="legend gt" aria-labelledby="g-legend">
      ${TRANSPORT_LEGEND.map((it) => `<li>${sample(it)}<span class="grow">${esc(it.label)}<small>${esc(it.note)}</small></span></li>`).join('')}
    </ul>
    <div class="note">${icon('lightbulb')}<div>Nhấp vào một con đường để mở thẻ <b>Giao thông</b>: lộ giới ước tính, mở rộng về phía nào bao nhiêu mét, công trình bị ảnh hưởng dọc tuyến. Mạng lưới đường được dựng từ ranh giới ô đất của đồ án phân khu (phần đất còn lại giữa các ô là đất giao thông); nơi chưa có đồ án phân khu dùng quy hoạch chung (kém chi tiết). Hệ thống chính thức không công bố lớp chỉ giới đường đỏ riêng, nên kết quả chỉ để tham khảo.</div></div>`;
}

/* ---------------- legend highlight (vector QH 1/2000) ---------------- */

/** Highlight the cells of a legend item on the map (null: clear). */
function setHighlight(plan, it) {
  const ts = it ? itemTypes(plan, it) : [];
  if (it && !ts.length) { toast('Không có ô nào thuộc loại này trên bản đồ', { type: 'info' }); return false; }
  if (!it && !hl.key) return false;
  Object.assign(hl, it ? { plan, key: legendKey(it), label: it.label, hex: it.hex || '#94a3b8', ts } : { plan: null, key: null, label: '', hex: '', ts: [] });
  pm.highlightTypes(hl.ts);
  renderPill();
  return true;
}

function renderPill() {
  const el = els.pill;
  if (!el) return;
  if (!hl.key) { el.hidden = true; el.textContent = ''; return; }
  const { cells, m2 } = typeStats(vectorManifest(), hl.ts);
  el.innerHTML = `<span class="swatch" style="--sw:${esc(hl.hex)}"></span>`
    + `<span class="hl-text"><span>Tô sáng: <b>${esc(hl.label)}</b></span><small>${fmt.num(cells)} ô · ${fmt.area(m2)} trên toàn lớp</small></span>`
    + `<button type="button" class="icon-btn ghost sm" data-act="hl-clear" aria-label="Bỏ tô sáng">${icon('x')}</button>`;
  el.hidden = false;
}

/** Merge legend entries that share label and colour (the source repeats some). */
function dedupeLegend(items) {
  const out = [];
  const seen = new Set();
  for (const it of items) {
    const k = fold(it.label) + '|' + (it.hex || '').toUpperCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

const pctPos = (v) => ((v - 15) / 85) * 100;

/* ---------------- events ---------------- */

function onClick(e) {
  const t = e.target.closest('[data-plan],[data-bm],[data-act]');
  if (!t) return;
  if (t.dataset.plan != null) { setPlan(planFrom(t.dataset.plan) ?? null); return; }
  if (t.dataset.bm) { setBasemap(t.dataset.bm); return; }
  if (t.dataset.act === 'legend-all') { legendState.all = !legendState.all; renderLegend(); }
  if (t.dataset.act === 'lg-hl') {
    const plan = prefs.plan;
    const it = dedupeLegend(legendOf(plan)?.items || []).find((x) => legendKey(x) === t.dataset.k);
    if (!it) return;
    const off = hl.plan === plan && hl.key === t.dataset.k;
    const changed = setHighlight(off ? null : plan, off ? null : it);
    renderLegend();
    if (changed && !off && isMobile()) closeDrawer(); // show the result; the pill keeps the way back
  }
  if (t.dataset.act === 'bm-tray') { if (isMobile()) closeDrawer(); bus.emit('tray', 'basemap'); }
  if (t.dataset.act === 'catalog') { if (isMobile()) closeDrawer(); bus.emit('catalog'); }
  if (t.dataset.act === 'gia-table') { if (isMobile()) closeDrawer(); bus.emit('gia-table', {}); }
}

function onInput(e) {
  if (e.target.id === 'opacity-range') {
    const v = +e.target.value;
    e.target.style.setProperty('--p', pctPos(v) + '%');
    $('#opacity-out', els.body).textContent = v + '%';
    setOpacity(v / 100);
  } else if (e.target.id === 'legend-q') {
    legendState.q = e.target.value;
    const pos = e.target.selectionStart;
    renderLegend();
    const inp = $('#legend-q', els.body);
    if (inp) { inp.focus(); inp.setSelectionRange(pos, pos); }
  }
}

function onChange(e) {
  if (e.target.id === 'labels-toggle') { setLabels(e.target.checked); return; }
  if (e.target.id === 'sdd-period') { legendState.q = ''; legendState.all = false; setSddPeriod(e.target.value); return; }
  if (e.target.id === 'gia-toggle') {
    setGiaLayer(e.target.checked);
    return;
  }
  if (e.target.id === 'places-toggle') {
    setPlaces(e.target.checked);
    const lg = $('#places-legend', els.body);
    if (lg) lg.hidden = !e.target.checked;
    return;
  }
  const n = +e.target.dataset.bound;
  if (n) setBoundary(n, e.target.checked);
}

/** Arrow keys move within radio groups (WAI-ARIA radio pattern). */
function onKey(e) {
  const r = e.target.closest('[role=radio]');
  if (!r || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
  e.preventDefault();
  const group = $$('[role=radio]', r.parentElement);
  const i = group.indexOf(r);
  const next = group[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : group.length - 1)) % group.length];
  next.click();
  requestAnimationFrame(() => {
    const sel = next.dataset.plan != null ? `[data-plan="${next.dataset.plan}"]` : `[data-bm="${next.dataset.bm}"]`;
    $(sel, els.body)?.focus();
  });
}

